import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
(() => {
  const root = document.querySelector('[data-card-sender]');
  if (!root) return;

  const productId = root.dataset.productId;
  const maxCards  = parseInt(root.dataset.maxCards, 10);
  const leadDays  = parseInt(root.dataset.leadDays, 10);

  const form         = root.querySelector('[data-cs-form]');
  const openBtn      = root.querySelector('[data-cs-open]');
  const closeBtn     = root.querySelector('[data-cs-close]');
  const recipientsEl = root.querySelector('[data-cs-recipients]');
  const template     = root.querySelector('[data-cs-recipient-template]');
  const errorEl      = root.querySelector('[data-cs-error]');
  const customQty    = root.querySelector('[data-cs-custom-qty]');

  // Mindest-Versanddatum (Werktage)
  const minDate = (() => {
    const d = new Date();
    let added = 0;
    while (added < leadDays) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) added++;
    }
    return d.toISOString().split('T')[0];
  })();

  const getQuantity = () => {
    const checked = form.querySelector('input[name="quantity"]:checked');
    if (!checked) return 1;
    if (checked.value === 'custom') {
      return Math.min(maxCards, Math.max(1, parseInt(customQty.value, 10) || 1));
    }
    return parseInt(checked.value, 10);
  };

  const renderRecipients = () => {
    const qty = getQuantity();
    const current = recipientsEl.children.length;
    if (qty > current) {
      for (let i = current; i < qty; i++) {
        const node = template.content.cloneNode(true);
        node.querySelector('.cs-rec-title').textContent = `Karte ${i + 1}`;
        const dateInput = node.querySelector('[data-cs-date]');
        dateInput.min = minDate;
        const ta = node.querySelector('textarea');
        const counter = node.querySelector('[data-cs-counter]');
        ta.addEventListener('input', () => {
          counter.textContent = `${ta.value.length} / 300`;
        });
        recipientsEl.appendChild(node);
      }
    } else if (qty < current) {
      while (recipientsEl.children.length > qty) {
        recipientsEl.removeChild(recipientsEl.lastChild);
      }
    }
  };

  openBtn.addEventListener('click', () => {
    form.hidden = false;
    openBtn.hidden = true;
    renderRecipients();
    form.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  closeBtn.addEventListener('click', () => {
    form.hidden = true;
    openBtn.hidden = false;
  });

  form.addEventListener('change', (e) => {
    if (e.target.name === 'quantity' || e.target === customQty) {
      renderRecipients();
    }
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorEl.hidden = true;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const qty = getQuantity();
    const properties = {
      'Absender': `${form.sender_name.value} <${form.sender_email.value}>`,
      'Anzahl Karten': String(qty)
    };

    [...recipientsEl.children].forEach((rec, idx) => {
      const n = idx + 1;
      const get = (f) => rec.querySelector(`[data-field="${f}"]`).value.trim();
      properties[`Karte ${n} – Empfänger`] =
        `${get('vorname')} ${get('nachname')}${get('firma') ? ', ' + get('firma') : ''}`;
      properties[`Karte ${n} – Adresse`] =
        `${get('strasse')}, ${get('plz')} ${get('ort')}, ${get('land')}`;
      properties[`Karte ${n} – Versand am`] = get('versand');
      properties[`Karte ${n} – Nachricht`]  = get('nachricht');
    });

    const submitBtn = form.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Wird vorbereitet…';

    try {
      await fetch('/cart/clear.js', { method: 'POST' });

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(productId, 10),
            quantity: qty,
            properties: properties
          }]
        })
      });

      if (!res.ok) throw new Error('Fehler beim Hinzufügen zum Warenkorb.');

      window.location.href = '/checkout';
    } catch (err) {
      errorEl.textContent = err.message;
      errorEl.hidden = false;
      submitBtn.disabled = false;
      submitBtn.textContent = 'Weiter zum Checkout →';
    }
  });
})();
